import { Grid } from '@mui/material';
import PageWrapper from 'components/Common/PageWraper';
import KpiCard from 'components/TableauObs/common/KpiCard';
import { CustomizedDataGrid } from 'components/customized_data_grid';
import React, { useCallback, useEffect, useState } from 'react';
import { 
    fetchCatalogueDataCompleteness, 
    fetchDataQualityAndRiskKpis, 
    fetchdataQualityBreakDown,
    fetchMissingProductDetailsTableData,
    fetchRenewalsFilterOptions,
    exportMissingDetails
} from '../../service/service';
import BillingCycleDistribution, { BillingCycleItem } from './BillingCycleDistribution';
import LicenseModelDistributionWidget from './LicenseModelDistributionWidget';

interface ProductCatalogDataQualityRiskProps {
    filters?: {
        filterOption?: string;
        startDate?: string;
        endDate?: string;
        filterList?: null | { [key: string]: any };
    };
    checkedListRef?: React.MutableRefObject<any>;
    resetFiltersRef?: React.MutableRefObject<(() => void) | null>;
}

const formatCurrency = (amount: number | null | undefined): string => {
    if (amount === null || amount === undefined || amount === 0) return '-';
    return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
    }).format(amount);
};

const DaysFormatter: React.FC<{ row: any }> = ({ row }) => {
    const days = row.daysToRenewal;
    if (days === undefined || days === null) return <span>-</span>;
    const displayValue = days < 0 ? `Overdue by ${Math.abs(days)} days` : `${days} days`;
    
    return (
        <span style={{ fontWeight: 500 }}>
            {displayValue}
        </span>
    );
};

const CostFormatter: React.FC<{ row: any }> = ({ row }) => {
    const cost = row.cost;
    const maskedCost = row.maskedCost;
    
    return (
        <span style={{ fontWeight: 500 }}>
            {maskedCost || formatCurrency(cost)}
        </span>
    );
};

const SeverityFormatter: React.FC<{ row: any }> = ({ row }) => {
    const severity = row.severity;
    
    const getSeverityColor = (severity: string) => {
        switch(severity) {
            case 'High':
                return '#D82B51';
            case 'Medium':
                return '#F57600';
            case 'Low':
                return '#01C008';
            default:
                return '#4A5A6A';
        }
    };
    
    const getSeverityBackground = (severity: string) => {
        switch(severity) {
            case 'High':
                return '#FFE5E8';
            case 'Medium':
                return '#FFF3E0';
            case 'Low':
                return '#E8F0FE';
            default:
                return '#F5F5F5';
        }
    };
    
    if (!severity) return <span>-</span>;
    
   return (
    <div style={{
        padding: '',
        textAlign: 'center'
    }}>
        <span style={{
            padding: '0px 18px',
            borderRadius: '4px',
            fontSize: '12px',
            fontWeight: 600,
            color: getSeverityColor(severity),
            backgroundColor: getSeverityBackground(severity),
            display: 'inline-block'
        }}>
            {severity}
        </span>
    </div>
);
};

const missingDetailsColumns = [
    { 
        key: "productName", 
        name: "Product Name", 
        minWidth: 250,
        formatter: ({ row }: any) => (
            <span style={{ fontWeight: 500 }}>{row.productName || '-'}</span>
        ),
        resizable: true,
        flexGrow: 2,
        sortable: true
    },
    { 
        key: "issueType", 
        name: "Issue Type", 
        minWidth: 280,
        formatter: ({ row }: any) => (
            <span style={{ fontWeight: 500 }}>
                {row.issueType || '-'}
            </span>
        ),
        resizable: true,
        flexGrow: 2,
        sortable: true
    },
    { 
        key: "severity", 
        name: "Severity Tag", 
        minWidth: 140,
        formatter: SeverityFormatter,
        resizable: true,
        flexGrow: 1,
        sortable: true
    },
    { 
        key: "daysToRenewal", 
        name: "Days to Renewal", 
        minWidth: 160,
        formatter: DaysFormatter,
        resizable: true,
        flexGrow: 1,
        sortable: true
    },
    { 
        key: "cost", 
        name: "Cost", 
        minWidth: 150,
        formatter: CostFormatter,
        resizable: true,
        flexGrow: 1,
        sortable: true
    }
];

const DataQualityRiskTab: React.FC<ProductCatalogDataQualityRiskProps> = ({ checkedListRef, filters: externalFilters, resetFiltersRef }) => {

    const [kpiLoadingState, setKpiLoadingState] = useState({
        isKpiLoading: true,
        isCatalogueDataCompletenessLoading: true,
        dataQualityBreakDown:true
    });

    const [renewalsWithoutBudget, setRenewalsWithoutBudget] = useState<number | null>(null);
    const [productsWithoutRenewalDate, setProductsWithoutRenewalDate] = useState<number | null>(null);
    const [renewalsWithoutOwner, setRenewalsWithoutOwner] = useState<number | null>(null);
    const [unussignedProducts, setUnussignedProducts] = useState<number | null>(null);
    const [dataCompletenessPercentage, setDataCompletenessPercentage] = useState<number | null>(null);
    const [catalogueDataCompleteness, setCatalogueDataCompleteness] = useState<any[]>([]);
    const [billingCycleDistData, setBillingCycleDistData] = useState<BillingCycleItem[]>([]);
    
    const [missingDetailsData, setMissingDetailsData] = useState<any[]>([]);
    const [missingDetailsLoading, setMissingDetailsLoading] = useState(false);
    const [missingDetailsPagination, setMissingDetailsPagination] = useState({
        currentPage: 1,
        totalPages: 0,
        totalElements: 0,
        pageSize: 100
    });
    const [missingDetailsSortConfig, setMissingDetailsSortConfig] = useState({
        sortBy: 'severity',
        sortDir: 'DESC' as 'ASC' | 'DESC'
    });
    const [missingDetailsFilters, setMissingDetailsFilters] = useState<Record<string, any>>({});
    const [missingDetailsSearch, setMissingDetailsSearch] = useState<Record<string, string>>({});
    const [filterDataOptions, setFilterDataOptions] = useState<Record<string, string[]>>({
        severity: [],
        issueType: []
    });
    const [filterLoading, setFilterLoading] = useState(false);
    const [catalogueDataCompletenessView, setCatalogueDataCompletenessView] = useState<'cost' | 'count'>('count');

    const setKpiLoading = (key: keyof typeof kpiLoadingState, value: boolean) => {
        setKpiLoadingState(prev => ({
            ...prev,
            [key]: value,
        }));
    };

    const fetchDataQualityAndRiskKpisData = useCallback(async () => {
        setKpiLoading('isKpiLoading', true);
        try {
            const response = await fetchDataQualityAndRiskKpis();
            if (response?.success && response?.data) {
                setRenewalsWithoutBudget(response.data.renewalsWithoutBudget ?? 0);
                setProductsWithoutRenewalDate(response.data.productsWithoutRenewalDate ?? 0);
                setRenewalsWithoutOwner(response.data.renewalsWithoutOwner ?? 0);
                setUnussignedProducts(response.data.unussignedProducts ?? 0)
                setDataCompletenessPercentage(response.data.dataCompletenessPercentage ?? 0);
            }
        } catch (error) {
            console.error('Error fetching data quality and risk KPIs:', error);
        } finally {
            setKpiLoading('isKpiLoading', false);
        }
    }, []);

     const fetchDataQualityBreakDownData = useCallback(async () => {
            setKpiLoading('dataQualityBreakDown', true);
            try {
                const response = await fetchdataQualityBreakDown();
                if (response?.success && Array.isArray(response.data)) {
                    setBillingCycleDistData(response.data);
                }
            } catch (error) {
                console.error('Error fetching Billing Cycle Distribution:', error);
            } finally {
                setKpiLoading('dataQualityBreakDown', false);
            }
        }, []);

    const fetchCatalogueDataCompletenessData = useCallback(async () => {
        setKpiLoading('isCatalogueDataCompletenessLoading', true);
        try {
            const response = await fetchCatalogueDataCompleteness();
            if (response?.data?.catalogueDataCompleteness) {
                setCatalogueDataCompleteness(response.data.catalogueDataCompleteness);
            }
        } catch (error) {
            console.error('Error fetching catalogue data completeness:', error);
        } finally {
            setKpiLoading('isCatalogueDataCompletenessLoading', false);
        }
    }, []);

    const fetchMissingDetailsTableData = useCallback(async (
        page = 1,
        pageSize = 100,
        sortBy = missingDetailsSortConfig.sortBy,
        sortDir = missingDetailsSortConfig.sortDir
    ) => {
        setMissingDetailsLoading(true);
        try {
            const payload = {
                page,
                pageSize,
                sortBy,
                orderBy: sortDir,
                filters: missingDetailsFilters,
                search: missingDetailsSearch
            };

            const response = await fetchMissingProductDetailsTableData(payload);
            
            if (response?.success && response?.data) {
                setMissingDetailsData(response.data.products || []);
                setMissingDetailsPagination(response.data.pagination || {
                    currentPage: page,
                    totalPages: 0,
                    totalElements: 0,
                    pageSize: pageSize
                });
            } else {
                setMissingDetailsData([]);
                setMissingDetailsPagination({
                    currentPage: page,
                    totalPages: 0,
                    totalElements: 0,
                    pageSize: pageSize
                });
            }
        } catch (error) {
            console.error('Error fetching missing details table data:', error);
            setMissingDetailsData([]);
        } finally {
            setMissingDetailsLoading(false);
        }
    }, [missingDetailsFilters, missingDetailsSearch, missingDetailsSortConfig.sortBy, missingDetailsSortConfig.sortDir]);


    const fetchFilterOptions = useCallback(async () => {
        setFilterLoading(true);
        try {
              const fieldsFilter = [ 'productName'];
                
                const optionsPromisesFilter = fieldsFilter.map(field => 
                    fetchRenewalsFilterOptions(field)
                        .then(data => ({ field, data }))
                        .catch(error => {
                            console.error(`Error fetching Filters For${field} options:`, error);
                            return { field, data: [] };
                        })
                );
    
                const resultsFilter = await Promise.all(optionsPromisesFilter);
                
                const optionsMap: Record<string, string[]> = {};
                resultsFilter.forEach(result => {
                    optionsMap[result.field] = result.data;
                });
            const severityOptions = ['High', 'Medium', 'Low'] ;
            
            const issueTypeOptions =  [
                'Unknown Renewal Date',
                'Missing Budget', 
                'Missing License Quantity', 
                'Missing Renewal Status', 
                'Missing Billing Cycle', 
                'Missing Owner'
            ];
            
            setFilterDataOptions({
                severity: severityOptions,
                issueType: issueTypeOptions,
                productName:optionsMap['productName']
            });
        } catch (error) {
            console.error('Error fetching filter options:', error);
            setFilterDataOptions({
                severity: ['High', 'Medium', 'Low'],
                issueType: [
                    'Unknown Renewal Date',
                    'Missing Budget', 
                    'Missing License Quantity', 
                    'Missing Renewal Status', 
                    'Missing Billing Cycle', 
                    'Missing Owner'
                ]
            });
        } finally {
            setFilterLoading(false);
        }
    }, []);

    const handleMissingDetailsFilterChange = useCallback((filter: { [key: string]: any }) => {
        const filterListMap: { [key: string]: any } = {};
        Object.keys(filter).forEach((key) => {
            const value = filter[key];
            if (value != null && (!Array.isArray(value) || value.length > 0)) {
                filterListMap[key] = value;
            }
        });
        setMissingDetailsFilters(filterListMap);
        setMissingDetailsPagination(prev => ({ ...prev, currentPage: 1 }));
        
        setTimeout(() => {
            fetchMissingDetailsTableData(1, missingDetailsPagination.pageSize);
        }, 300);
    }, [fetchMissingDetailsTableData, missingDetailsPagination.pageSize]);

    const handleMissingDetailsSearch = useCallback((searchDetails: any, columnKey: any) => {
        setMissingDetailsSearch(prev => ({
            ...prev,
            [columnKey]: searchDetails
        }));
        setMissingDetailsPagination(prev => ({ ...prev, currentPage: 1 }));
        
        setTimeout(() => {
            fetchMissingDetailsTableData(1, missingDetailsPagination.pageSize);
        }, 300);
    }, [fetchMissingDetailsTableData, missingDetailsPagination.pageSize]);

    const handleMissingDetailsSorting = useCallback((sorting: any) => {
        setMissingDetailsSortConfig({
            sortBy: sorting.sortBy,
            sortDir: sorting.sortDir
        });
        setMissingDetailsPagination(prev => ({ ...prev, currentPage: 1 }));
        
        fetchMissingDetailsTableData(
            1, 
            missingDetailsPagination.pageSize, 
            sorting.sortBy, 
            sorting.sortDir
        );
    }, [fetchMissingDetailsTableData, missingDetailsPagination.pageSize]);

    const handleMissingDetailsPageChange = useCallback((event: any, value: any) => {
        setMissingDetailsPagination(prev => ({ ...prev, currentPage: value }));
        fetchMissingDetailsTableData(
            value, 
            missingDetailsPagination.pageSize,
            missingDetailsSortConfig.sortBy,
            missingDetailsSortConfig.sortDir
        );
    }, [fetchMissingDetailsTableData, missingDetailsPagination.pageSize, missingDetailsSortConfig]);

    const handleMissingDetailsPageSizeChange = useCallback((event: any) => {
        const newSize = Number.parseInt(event.target.value);
        setMissingDetailsPagination(prev => ({ ...prev, pageSize: newSize, currentPage: 1 }));
        fetchMissingDetailsTableData(
            1, 
            newSize,
            missingDetailsSortConfig.sortBy,
            missingDetailsSortConfig.sortDir
        );
    }, [fetchMissingDetailsTableData, missingDetailsSortConfig]);

    const handleExport = useCallback(async () => {
        try {
            const exportData = {
                formatType: "EXCEL",
                filters: missingDetailsFilters,
                search: missingDetailsSearch,
                sortBy: missingDetailsSortConfig.sortBy,
                orderBy: missingDetailsSortConfig.sortDir,
                exportColumns: ['Product Name', 'Issue Type', 'Severity Tag', 'Days to Renewal', 'Cost']
            };
            await exportMissingDetails(exportData);
        } catch (error) {
            console.error('Error exporting missing details data:', error);
        }
    }, [missingDetailsFilters, missingDetailsSearch, missingDetailsSortConfig]);

    useEffect(() => {
        fetchDataQualityAndRiskKpisData();
        fetchCatalogueDataCompletenessData();
        fetchDataQualityBreakDownData();
        fetchFilterOptions();
        fetchMissingDetailsTableData();
    }, [fetchDataQualityAndRiskKpisData, fetchCatalogueDataCompletenessData, fetchDataQualityBreakDownData, fetchFilterOptions, fetchMissingDetailsTableData]);

    const getDataCompletenessColor = (percentage: number) => {
        if (percentage >= 95) return '#4caf50';
        if (percentage >= 80) return '#ffbf00';
        return '#f44336';
    };

    const completenessColorMapping = {
        'Complete Records': '#00bcd4', // Teal
        'Partial Records': '#4caf50', // Green
        'Incomplete Records': '#ff9800', // Orange
    };

    return (
        <PageWrapper sx={{ paddingTop: '32px !important' }}>
            <Grid container spacing={3} sx={{ mb: 2 }}>
                <Grid
                    item
                    xs={12}
                    sx={{
                    display: "grid",
                    gap: 2,
                    gridTemplateColumns: {
                        xs: "repeat(1, 1fr)",
                        sm: "repeat(2, 1fr)",   
                        md: "repeat(3, 1fr)",   
                        lg: "repeat(5, 1fr)",   
                    },
                    }}
                >
                    <KpiCard
                        title="Renewals Without Budget"
                        number={renewalsWithoutBudget ?? 0}
                        isLoading={kpiLoadingState.isKpiLoading || renewalsWithoutBudget === null}
                        hasTooltip
                        tooltipLabel="Missing budget for upcoming renewals"
                        hasDropdown={false}
                        theme="light"
                        isCustomLabel={true}
                        customLabelText="Next 90 Days"
                        containerProps={{
                            sx: { minHeight: "180px" },
                        }}
                    />
                    <KpiCard
                        title="Products without Renewal Dates"
                        number={productsWithoutRenewalDate ?? 0}
                        isLoading={kpiLoadingState.isKpiLoading || productsWithoutRenewalDate === null}
                        hasTooltip
                        tooltipLabel="Products that have no renewal date set"
                        hasDropdown={false}
                        theme="light"
                        isCustomLabel={true}
                        customLabelText="Contracts At Risk"
                        containerProps={{
                            sx: { minHeight: "180px" },
                        }}
                    />
                    <KpiCard
                        title="Renewals Without Owner"
                        number={renewalsWithoutOwner ?? 0}
                        isLoading={kpiLoadingState.isKpiLoading || renewalsWithoutOwner === null}
                        hasTooltip
                        tooltipLabel="Renewals with no assigned owner"
                        hasDropdown={false}
                        theme="light"
                        isCustomLabel={true}
                        customLabelText="High-Risk Renewals"
                        containerProps={{
                            sx: { minHeight: "180px" },
                        }}
                    />
                    <KpiCard
                        title="Unassigned Products"
                        number={unussignedProducts ?? 0}
                        isLoading={kpiLoadingState.isKpiLoading || unussignedProducts === null}
                        hasTooltip
                        tooltipLabel="Products not assigned to any owner"
                        hasDropdown={false}
                        theme="light"
                        isCustomLabel={true}
                        customLabelText="Not Monitored"
                        containerProps={{
                            sx: { minHeight: "180px" },
                        }}
                    />
                    <KpiCard
                        title="Data Completeness"
                        number={dataCompletenessPercentage ?? 0}
                        isPercentage={true}
                        isLoading={kpiLoadingState.isKpiLoading || dataCompletenessPercentage === null}
                        hasTooltip
                        tooltipLabel="Percentage of products with required fields filled"
                        hasDropdown={false}
                        theme="light"
                        isCustomLabel={true}
                        customLabelText="Target: 95%"
                        containerProps={{
                            sx: { minHeight: "180px" },
                        }}
                        numbersx={{ color: getDataCompletenessColor(dataCompletenessPercentage ?? 0) }}
                    />
                </Grid>
            </Grid>
            <Grid container spacing={2} sx={{ mb: 2 }}>
                <Grid item xs={12} md={6}>
                    <LicenseModelDistributionWidget
                        title="Catalogue Data Completeness"
                        data={catalogueDataCompleteness}
                        colorMapping={completenessColorMapping}
                        isLoading={kpiLoadingState.isCatalogueDataCompletenessLoading || catalogueDataCompleteness === null}
                        hasTooltip={true}
                        hasToggle={true}
                        viewType={catalogueDataCompletenessView}
                        onViewTypeChange={(view) => setCatalogueDataCompletenessView(view)}
                        tooltipText="Shows complete, partial, and incomplete records. Toggle to view by count or cost."
                        centerLabel="Total"
                        containerProps={{ sx: { height: '100%', minHeight: '300px' } }}
                        showValueAsCount={true}
                        valueSuffix="Products"
                    />
                </Grid>
                <Grid item xs={12} md={6}>
                    <BillingCycleDistribution
                        data={billingCycleDistData}
                        isLoading={kpiLoadingState.dataQualityBreakDown}
                        hasTooltip={true}
                        tooltipText="Missing required fields across products"
                        customBarColor="#fe2626"
                        customBackgroundColor="#F0F8FF"
                        isNumberBold={true}
                        title='Data Quality Breakdown'
                    />
                </Grid>    
            </Grid>
            
            {/* Missing Details Table Section */}
            <Grid container spacing={2} sx={{ mt: 2 }}>
                <Grid item xs={12}>
                    <CustomizedDataGrid
                        title="Missing Data Details- Action Required"
                        tableColumns={missingDetailsColumns}
                        tableData={missingDetailsData}
                        tableType="missing-details"
                        defaultColumnNames={['Product Name', 'Issue Type', 'Severity Tag', 'Days to Renewal', 'Cost']}
                        onSorting={handleMissingDetailsSorting}
                        currSorting={{
                            sortBy: missingDetailsSortConfig.sortBy,
                            sortDir: missingDetailsSortConfig.sortDir
                        }}
                        filterDataOptions={filterDataOptions}
                        searchDataValues={missingDetailsSearch}
                        onFilterCheck={handleMissingDetailsFilterChange}
                        currFilter={missingDetailsFilters}
                        onFilterSearch={handleMissingDetailsSearch}
                        onHeaderSelectAll={() => {}}
                        onFilterClick={() => {}}
                        showExport={true}
                        fileName={`missing-details-report-${new Date().toISOString().split('T')[0]}`}
                        tableName="missing-details"
                        formatType="EXCEL"
                        exportColumns={['Product Name', 'Issue Type', 'Severity Tag', 'Days to Renewal', 'Cost']}
                        exportTableData={handleExport}
                        isDateRangeFilter={false}
                        isDisabled={false}
                        isRenderPagination={true}
                        isShowMoreButton={true}
                        page={missingDetailsPagination.currentPage}
                        pageSize={missingDetailsPagination.pageSize}
                        paginationData={missingDetailsPagination}
                        listLength={missingDetailsData.length}
                        handlepageSizeChange={handleMissingDetailsPageSizeChange}
                        handlePageChange={handleMissingDetailsPageChange}
                        pageSizeOptions={[100,200,500,1000]}
                        isFetching={missingDetailsLoading || filterLoading}
                        isLighterTheme={true}
                    />
                </Grid>
            </Grid>
        </PageWrapper>
    );
};

export default DataQualityRiskTab;